import React from 'react';
import { useTranslation } from 'react-i18next';
import { useObjectiveQuery } from '../../../../__generated__/graphql';
import {
  mapEmployeeOption,
  mapRelatedGoal,
  toObjectiveIdInput,
} from '../mappings';
import { GoalId } from '../types';
import { useGoalSelectOptions } from '../useGoalSelectOptions';
import { GoalCommonFieldsSkeleton } from './GoalCommonFieldsSkeleton';
import { GoalSidebarMode } from './types';
import { mapToObjectiveFormData } from './mappings';
import { mapActivities } from '../GoalsActivities/mappings';
import { GoalUpdateForm } from './GoalUpdateForm';
import { mapGoalHierarchy } from './GoalHierarchy/mappings';

type Props = {
  goalId: GoalId;
  mode: GoalSidebarMode;
  onClose: () => void;
};

export const GoalSidebarUpdateContent: React.FC<Props> = ({
  goalId,
  mode,
  onClose,
}) => {
  const { t } = useTranslation();

  const {
    options,
    error: optionsError,
    loading: optionsLoading,
  } = useGoalSelectOptions();

  const { data, error, loading } = useObjectiveQuery({
    variables: {
      objectiveId: toObjectiveIdInput(goalId),
    },
  });

  const isLoading = optionsLoading || loading || !data || !options;

  if (error || optionsError) {
    return <div>{t(`performanceReview.goals2.errorLoading`)}</div>;
  }

  if (isLoading) {
    return <GoalCommonFieldsSkeleton />;
  }
  if (!data?.objective) {
    return <div>{t(`performanceReview.goals2.goalNotFound`)}</div>;
  }

  const formData = mapToObjectiveFormData(data.objective);
  const goalWithHierarchy = mapGoalHierarchy(data.objective);
  const alignedTo =
    data.objective.__typename === 'EmployeeObjective'
      ? data.objective.alignedTo.map(mapRelatedGoal)
      : [];

  const owner = data.objective.owner
    ? mapEmployeeOption(data.objective.owner)
    : undefined;

  const activities = mapActivities(data.objective, options);

  //This is done to handle the case when some goals have already inactive periods
  const periodsToShow = (options?.allPeriods ?? []).filter((p) => {
    if (p.active) {
      return true;
    } else {
      return formData.timePeriodId === p.value;
    }
  });

  return (
    <GoalUpdateForm
      goalId={goalId}
      activities={activities}
      mode={mode}
      owner={owner}
      formData={formData}
      goalWithHierarchy={goalWithHierarchy}
      onClose={onClose}
      options={{ ...options, periods: periodsToShow }}
      alignedTo={alignedTo}
    />
  );
};
